import { useEffect, useRef } from 'react';
import { Chart, type ChartConfiguration } from 'chart.js/auto';
import { ChartDataGrid } from '../../../shared/dashboard/ChartDataGrid';
import { MetricInfo } from '../../../shared/dashboard/MetricInfo';
import { UnavailableState } from '../../../shared/dashboard/UnavailableState';
import type { MetricDictionaryView } from '../../dashboard-v2/useMetricDictionary';
import { formatCurrency, type ExecutivePeriod, type ExecutiveTrendPoint } from '../../dashboard-v2/types';

const GROSS_COLOR = '#1577bd';
const EMPLOYER_TAX_COLOR = '#0e588d';

/**
 * Twelve calendar months of payroll cost as stacked bars: gross wages plus employer payroll taxes, so each bar's height is that month's total employer cost.
 * The bars come from the server's monthly points, which are slices of the same list of runs as the headline, so the months inside the chosen period add up to
 * the headline exactly; the table alternative shows that sum. When payroll cannot be read the chart says "Data unavailable"; it never draws zeros.
 */
export function LaborCostTrend({
  trend, period, metrics,
}: {
  trend: ExecutiveTrendPoint[] | null;
  period: ExecutivePeriod;
  metrics: MetricDictionaryView;
}) {
  return (
    <section className="ed-card ed-trend-card" data-testid="labor-cost-trend-card" aria-labelledby="ed-trend-title">
      <h3 id="ed-trend-title" className="ed-chart-title">
        Labor Cost, Last 12 Months
        <MetricInfo metric={metrics.get('total-employer-payroll-cost')} />
      </h3>
      {trend === null ? (
        <UnavailableState reason="The payroll service could not be reached, so the monthly figures cannot be shown. Other sections still work." />
      ) : (
        <TrendBody trend={trend} period={period} />
      )}
    </section>
  );
}

function TrendBody({ trend, period }: { trend: ExecutiveTrendPoint[]; period: ExecutivePeriod }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    // No drawing surface (a browser that blocks canvas, or a test environment): skip the chart. The table below carries every number, so nothing is lost.
    if (!canvas || !canvas.getContext('2d')) return undefined;

    const colors = () => {
      const styles = getComputedStyle(canvas);
      return {
        text: styles.getPropertyValue('--ed-text-muted').trim() || '#6b7280',
        grid: styles.getPropertyValue('--ed-border').trim() || '#e7e9ee',
      };
    };

    const config: ChartConfiguration<'bar'> = {
      type: 'bar',
      data: {
        labels: trend.map((p) => p.label),
        datasets: [
          { label: 'Gross wages', data: trend.map((p) => p.grossWages), backgroundColor: GROSS_COLOR, stack: 'cost' },
          { label: 'Employer payroll taxes', data: trend.map((p) => p.employerTaxes), backgroundColor: EMPLOYER_TAX_COLOR, stack: 'cost' },
        ],
      },
      options: {
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom' },
          tooltip: {
            callbacks: {
              label: (item) => `${item.dataset.label}: ${formatCurrency(Number(item.raw))}`,
              footer: (items) => `Total: ${formatCurrency(items.reduce((s, i) => s + Number(i.raw), 0))}`,
            },
          },
        },
        scales: {
          x: { stacked: true, grid: { display: false } },
          y: { stacked: true, beginAtZero: true, ticks: { callback: (v) => formatCurrency(Number(v)).replace('.00', '') } },
        },
      },
    };

    const chart = new Chart(canvas, config);
    const applyTheme = () => {
      const c = colors();
      Chart.defaults.color = c.text;
      for (const scale of Object.values(chart.options.scales ?? {})) {
        if (scale && 'ticks' in scale && scale.ticks) scale.ticks.color = c.text;
        if (scale && 'grid' in scale && scale.grid) scale.grid.color = c.grid;
      }
      if (chart.options.plugins?.legend?.labels) chart.options.plugins.legend.labels.color = c.text;
      chart.update();
    };
    applyTheme();
    // A canvas cannot be restyled by CSS: redraw when the app switches between light and dark.
    const observer = new MutationObserver(applyTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => {
      observer.disconnect();
      chart.destroy();
    };
  }, [trend]);

  const startMonth = `${period.start.slice(0, 7)}-01`;
  const inPeriod = trend.filter((p) => p.monthStart >= startMonth && p.monthStart <= period.end);
  const latest = trend[trend.length - 1];
  const summary = latest
    ? `Stacked bar chart of employer payroll cost for each of the last 12 months. ${latest.label}: ${formatCurrency(latest.totalEmployerCost)}.`
    : 'No months to show.';

  return (
    <>
      <div className="ed-trend-chart" role="img" aria-label={summary}>
        <canvas ref={canvasRef} />
      </div>
      <ChartDataGrid
        caption="Employer payroll cost by month"
        rows={trend}
        rowKey={(p) => p.monthStart}
        firstColumn={{ header: 'Month', render: (p) => p.label }}
        columns={[
          { header: 'Gross wages', numeric: true, render: (p) => formatCurrency(p.grossWages) },
          { header: 'Employer payroll taxes', numeric: true, render: (p) => formatCurrency(p.employerTaxes) },
          { header: 'Total employer cost', numeric: true, render: (p) => formatCurrency(p.totalEmployerCost) },
          { header: 'Employees paid', numeric: true, render: (p) => String(p.employeesPaid) },
        ]}
        footer={inPeriod.length > 0 ? {
          label: `Total, ${period.label}`,
          cells: [
            formatCurrency(inPeriod.reduce((s, p) => s + p.grossWages, 0)),
            formatCurrency(inPeriod.reduce((s, p) => s + p.employerTaxes, 0)),
            formatCurrency(inPeriod.reduce((s, p) => s + p.totalEmployerCost, 0)),
            '',
          ],
        } : undefined}
      />
    </>
  );
}
